import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Dialog as MaterialDialog,
} from '@mui/material';

import { colors } from 'utils/constants';

const DialogErrorText = styled(DialogContentText)`
  color: ${colors.warning};
`;

/**
 * The project-standard confirmation dialog wrapping MUI `Dialog`: title,
 * optional subtitle, optional warning-colored error line, arbitrary content,
 * and a fixed Cancel / confirm button row. Use for modals where the parent
 * controls open state and handles the confirm action.
 */
const Dialog = ({
  confirmationText,
  dialogError,
  disabledButton,
  children,
  handleConfirm,
  handleDialogClose,
  isDialogOpen,
  subtitle,
  title,
}) => (
  <MaterialDialog
    aria-labelledby="dialog-title"
    onClose={handleDialogClose}
    fullWidth
    open={isDialogOpen}
  >
    <DialogTitle id="dialog-title">{title}</DialogTitle>
    <DialogContent>
      <DialogContentText>{subtitle}</DialogContentText>
      {dialogError && <DialogErrorText>{dialogError}</DialogErrorText>}
      {children}
    </DialogContent>
    <DialogActions>
      <Button color="primary" variant="text" onClick={handleDialogClose}>
        Cancel
      </Button>
      {/* Conditional used to allow removing confirmation step if, e.g. no options available. */}
      {confirmationText && (
        <Button
          data-testid="confirm-button"
          color="secondary"
          disabled={disabledButton}
          onClick={handleConfirm}
          type="button"
          variant="contained"
        >
          {confirmationText}
        </Button>
      )}
    </DialogActions>
  </MaterialDialog>
);

Dialog.defaultProps = {
  confirmationText: 'Save',
  dialogError: null,
  disabledButton: false,
  subtitle: '',
};

Dialog.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)])
    .isRequired,
  /** Confirm button label; when falsy the confirm button is omitted entirely, leaving only Cancel. */
  confirmationText: PropTypes.string,
  /** Error message shown in warning color between the subtitle and the dialog content. */
  dialogError: PropTypes.string,
  /** Disables the confirm button only (e.g. while a request is in flight). */
  disabledButton: PropTypes.bool,
  handleConfirm: PropTypes.func.isRequired,
  handleDialogClose: PropTypes.func.isRequired,
  isDialogOpen: PropTypes.bool.isRequired,
  subtitle: PropTypes.string,
  title: PropTypes.string.isRequired,
};

export default Dialog;
